import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '@/lib/api'
import { Skeleton } from '@/components/States'
import { IX } from './infraExtApi'

/**
 * A17.02-22 uchun umumiy kichik yordamchilar (InfraMonitorScreens'даги naqshni
 * takrorlaydi, lekin bir joyda — 3 ta ekran-fayl import qiladi). Faqat UI.
 */

/** Abortable fetch + reload; har ekran shundan foydalanadi. */
export function useLoad<T>(fn: (s: AbortSignal) => Promise<T>) {
  const [data, setData] = useState<T | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fn(ac.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])
  const reload = useCallback(() => setTick((t) => t + 1), [])
  return { data, err, reload, setData }
}

export function RefreshBtn({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
    >
      {IX.refresh}
    </button>
  )
}

export function Rows({ n = 8 }: { n?: number }) {
  return (
    <div className="space-y-1.5 p-4">
      {Array.from({ length: n }).map((_, i) => (
        <Skeleton key={i} className="h-7 w-full" />
      ))}
    </div>
  )
}

/** Kichik statistika kartasi (label + katta qiymat). */
export function Stat({ label, value, tone }: { label: string; value: string; tone?: 'success' | 'warning' | 'danger' }) {
  const color = tone === 'success' ? 'text-success' : tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <p className="text-xs text-tertiary">{label}</p>
      <p className={`mt-0.5 truncate text-lg font-semibold tabular ${color}`}>{value}</p>
    </div>
  )
}

/** Foiz-gauge (donut yo'q — oddiy bar, yengil). */
export function Gauge({ label, pct }: { label: string; pct: number }) {
  const p = Math.max(0, Math.min(100, pct))
  const tone = p >= 90 ? 'bg-danger' : p >= 70 ? 'bg-warning' : 'bg-success'
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-sm text-secondary">{label}</span>
        <span className="text-xl font-semibold text-primary tabular">{p.toFixed(0)}%</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-surface-3">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  )
}

export function fmtDuration(sec: number): string {
  if (sec <= 0) return '—'
  const d = Math.floor(sec / 86400)
  const h = Math.floor((sec % 86400) / 3600)
  const m = Math.floor((sec % 3600) / 60)
  return [d && `${d}д`, h && `${h}ч`, `${m}м`].filter(Boolean).join(' ')
}
